import { useState } from "react";
import type { ReactElement } from "react";
import { Button } from "@a2z/ui";
import { DrawerSheet } from "@/shared/components/dialogs/DrawerSheet";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { FilterAccordion } from "@/modules/orders/components/orders-filters/FilterAccordion";
import { RadioRow } from "@/modules/orders/components/orders-filters/RadioRow";
import { DateField } from "@/modules/orders/components/orders-filters/DateField";
import {
  TRANSACTION_PROVIDER_VALUES,
  TRANSACTION_STATUS_VALUES,
  getProviderLabel,
  getTransactionStatusLabel,
} from "@/modules/transactions/lib/transaction-display";
import {
  TRANSACTION_FILTER_ALL,
  type TransactionFiltersProps,
} from "@/modules/transactions/types/transactions.types";

/**
 * Transactions toolbar: a search box plus a Filters drawer holding status,
 * provider and date range — the same shape as the orders page's filters, which
 * keeps every control reachable at 375px instead of cramming selects into a row
 * that would overflow.
 *
 * Filters apply immediately (no draft/Apply round trip), so the drawer's footer
 * only offers Reset and Done.
 */
export function TransactionsFilters({
  value,
  onChange,
  onReset,
  activeCount,
}: TransactionFiltersProps): ReactElement {
  const t = useTranslations();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>("status");

  const toggleSection = (id: string): void => {
    setOpenSection((current) => (current === id ? null : id));
  };

  const statusSummary =
    value.status === TRANSACTION_FILTER_ALL
      ? t(keys.common.all)
      : getTransactionStatusLabel(t, value.status);

  const providerSummary =
    value.provider === TRANSACTION_FILTER_ALL
      ? t(keys.common.all)
      : getProviderLabel(t, value.provider);

  const dateSummary =
    value.from || value.to
      ? `${value.from || "…"} → ${value.to || "…"}`
      : t(keys.common.all);

  return (
    <>
      <div className="flex items-center gap-2">
        <div className="flex h-10 min-w-0 flex-1 items-center gap-2.5 rounded-[11px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-3">
          <SearchIcon />
          <input
            type="search"
            value={value.search}
            onChange={(event) =>
              onChange({ ...value, search: event.target.value })
            }
            placeholder={t(keys.transactions.searchPlaceholder)}
            aria-label={t(keys.transactions.searchAria)}
            className="min-w-0 flex-1 bg-transparent text-[13px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)]"
          />
        </div>

        <button
          type="button"
          onClick={() => setIsDrawerOpen(true)}
          className="inline-flex h-10 shrink-0 items-center gap-2 rounded-[11px] border border-[color:var(--border-light)] bg-[color:var(--card)] px-3 text-[13px] font-semibold text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--hover)]"
        >
          <FilterIcon />
          <span className="hidden sm:inline">
            {t(keys.transactions.filtersAction)}
          </span>
          {activeCount > 0 ? (
            <span className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-[color:var(--accent-500)] px-1.5 text-[10.5px] font-bold leading-[18px] text-[color:var(--on-accent)]">
              {activeCount}
            </span>
          ) : null}
        </button>
      </div>

      <DrawerSheet
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        size="sm"
        title={t(keys.transactions.filtersTitle)}
        closeLabel={t(keys.common.close)}
        footerActions={
          <>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onReset}
            >
              {t(keys.common.reset)}
            </Button>
            <Button
              type="button"
              className="flex-1"
              onClick={() => setIsDrawerOpen(false)}
            >
              {t(keys.common.done)}
            </Button>
          </>
        }
      >
        <FilterAccordion
          id="status"
          title={t(keys.transactions.filterStatus)}
          summary={statusSummary}
          active={value.status !== TRANSACTION_FILTER_ALL}
          open={openSection === "status"}
          onToggle={toggleSection}
          toggleAria={t(keys.transactions.filterStatus)}
          activeLabel={t(keys.common.active)}
        >
          <div className="flex flex-col gap-0.5">
            <RadioRow
              label={t(keys.common.all)}
              active={value.status === TRANSACTION_FILTER_ALL}
              onClick={() =>
                onChange({ ...value, status: TRANSACTION_FILTER_ALL })
              }
            />
            {TRANSACTION_STATUS_VALUES.map((status) => (
              <RadioRow
                key={status}
                label={getTransactionStatusLabel(t, status)}
                active={value.status === status}
                onClick={() => onChange({ ...value, status })}
              />
            ))}
          </div>
        </FilterAccordion>

        <FilterAccordion
          id="provider"
          title={t(keys.transactions.filterProvider)}
          summary={providerSummary}
          active={value.provider !== TRANSACTION_FILTER_ALL}
          open={openSection === "provider"}
          onToggle={toggleSection}
          toggleAria={t(keys.transactions.filterProvider)}
          activeLabel={t(keys.common.active)}
        >
          <div className="flex flex-col gap-0.5">
            <RadioRow
              label={t(keys.common.all)}
              active={value.provider === TRANSACTION_FILTER_ALL}
              onClick={() =>
                onChange({ ...value, provider: TRANSACTION_FILTER_ALL })
              }
            />
            {TRANSACTION_PROVIDER_VALUES.map((provider) => (
              <RadioRow
                key={provider}
                label={getProviderLabel(t, provider)}
                active={value.provider === provider}
                onClick={() => onChange({ ...value, provider })}
              />
            ))}
          </div>
        </FilterAccordion>

        <FilterAccordion
          id="date"
          title={t(keys.transactions.filterDateRange)}
          summary={dateSummary}
          active={value.from.length > 0 || value.to.length > 0}
          open={openSection === "date"}
          onToggle={toggleSection}
          toggleAria={t(keys.transactions.filterDateRange)}
          activeLabel={t(keys.common.active)}
        >
          {/* Two short, logically-related fields — the sanctioned 2-column case.
              Collapses to one column below `sm`. */}
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            <DateField
              label={t(keys.transactions.filterFrom)}
              value={value.from}
              max={value.to || undefined}
              onChange={(next) => onChange({ ...value, from: next })}
            />
            <DateField
              label={t(keys.transactions.filterTo)}
              value={value.to}
              min={value.from || undefined}
              onChange={(next) => onChange({ ...value, to: next })}
            />
          </div>
        </FilterAccordion>
      </DrawerSheet>
    </>
  );
}

function SearchIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0 text-[color:var(--text-muted)]"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4-4" />
    </svg>
  );
}

function FilterIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0"
    >
      <path d="M3 5h18M6 12h12M10 19h4" />
    </svg>
  );
}
